import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Shield, Users, TrendingUp, DollarSign, Activity, 
  Settings, LogOut, Search, Edit2, Check, X, Crown
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';
import { formatPair } from '../utils/commodities';

import { fmtFixed } from '../utils/format';
const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('analytics');
  const [adminInfo, setAdminInfo] = useState(null);
  
  // Analytics state
  const [analytics, setAnalytics] = useState(null);
  
  // Users state
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingBalance, setEditingBalance] = useState(null);
  
  // Trades state
  const [binaryTrades, setBinaryTrades] = useState([]);
  const [investments, setInvestments] = useState([]);
  
  // Loading states
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // AdminRoute already verified authentication, just load admin data
    const storedAdmin = localStorage.getItem('adminInfo');
    
    try {
      const adminData = JSON.parse(storedAdmin);
      setAdminInfo(adminData);
      console.log('Admin loaded:', adminData);
      loadAnalytics();
    } catch (error) {
      console.error('Error parsing admin info:', error);
      // If there's an error, AdminRoute will handle redirect on next render
    }
  }, []);

  const loadAnalytics = async () => {
    try {
      const response = await api.get('/admin/analytics');
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error loading analytics:', error);
      toast.error('Failed to load analytics');
    }
  };

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/users', {
        params: { search: searchTerm, limit: 10000 }
      });
      setUsers(response.data?.users || response.data || []);
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const loadBinaryTrades = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/binary-trades', {
        params: { status: 'active' }
      });
      setBinaryTrades(response.data?.trades || response.data || []);
    } catch (error) {
      console.error('Error loading trades:', error);
      toast.error('Failed to load binary trades');
    } finally {
      setLoading(false);
    }
  };

  const loadInvestments = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/arbitrage-investments', {
        params: { status: 'active' }
      });
      setInvestments(response.data);
    } catch (error) {
      console.error('Error loading investments:', error);
      toast.error('Failed to load investments');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateBalance = async (userId, newBalanceStr, currentBalance, reason) => {
    try {
      const newBalance = parseFloat(newBalanceStr);
      if (isNaN(newBalance)) {
        toast.error('Invalid balance amount');
        return;
      }
      const delta = newBalance - (currentBalance || 0);
      if (delta === 0) {
        setEditingBalance(null);
        return;
      }
      // Use canonical balance-adjust endpoint (writes audit log + sends notification)
      await api.post('/admin/balance/adjust', {
        user_id: userId,
        amount: Math.abs(delta),
        adjustment_type: delta > 0 ? 'add' : 'subtract',
        reason: reason || 'Manual adjustment by admin'
      });
      toast.success('Balance updated successfully');
      setEditingBalance(null);
      loadUsers();
    } catch (error) {
      console.error('Error updating balance:', error);
      toast.error(error.response?.data?.detail || 'Failed to update balance');
    }
  };

  const handleControlTrade = async (tradeId, outcome) => {
    if (!window.confirm(`Are you sure you want to set this trade to ${outcome.toUpperCase()}?`)) {
      return;
    }

    try {
      await api.post('/admin/binary-trades/control-outcome', {
        trade_id: tradeId,
        outcome: outcome
      });
      toast.success(`Trade outcome set to ${outcome.toUpperCase()}`);
      loadBinaryTrades();
      loadAnalytics();
    } catch (error) {
      console.error('Error controlling trade:', error);
      toast.error(error.response?.data?.detail || 'Failed to control trade outcome');
    }
  };

  const handleControlInvestment = async (investmentId, outcome, multiplier = 1.0) => {
    if (!window.confirm(`Are you sure you want to set this investment to ${outcome.toUpperCase()} with ${multiplier}x multiplier?`)) {
      return;
    }

    try {
      await api.post('/admin/arbitrage-investments/control-outcome', {
        investment_id: investmentId,
        outcome: outcome,
        profit_multiplier: parseFloat(multiplier)
      });
      toast.success(`Investment outcome set to ${outcome.toUpperCase()}`);
      loadInvestments();
      loadAnalytics();
    } catch (error) {
      console.error('Error controlling investment:', error);
      toast.error(error.response?.data?.detail || 'Failed to control investment outcome');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminInfo');
    navigate('/admin/login');
    toast.success('Logged out successfully');
  };

  useEffect(() => {
    if (activeTab === 'users') loadUsers();
    if (activeTab === 'trades') {
      loadBinaryTrades();
      loadInvestments();
    }
  }, [activeTab, searchTerm]);

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Admin Header */}
      <div className="bg-gray-800 border-b border-purple-500/30 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Shield className="h-8 w-8 text-purple-500" />
              <div>
                <h1 className="text-xl font-bold text-white">Phantom Trades Admin</h1>
                <p className="text-xs text-gray-400">Control Panel</p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            <div className="text-right">
              <p className="text-sm font-bold text-white">{adminInfo?.name}</p>
              <p className="text-xs text-gray-400">{adminInfo?.role}</p>
            </div>
            <button
              onClick={handleLogout}
              className="p-2 bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
            >
              <LogOut className="h-5 w-5 text-white" />
            </button>
          </div>
        </div>
      </div>

      {/* Admin Navigation */}
      <div className="bg-gray-800/50 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex space-x-1">
            <button
              onClick={() => setActiveTab('analytics')}
              className={`px-6 py-4 font-bold transition-all ${
                activeTab === 'analytics'
                  ? 'bg-purple-600 text-white border-b-4 border-purple-400'
                  : 'text-gray-400 hover:text-white hover:bg-gray-700/50'
              }`}
            >
              <Activity className="h-5 w-5 inline mr-2" />
              Analytics
            </button>
            <button
              onClick={() => setActiveTab('users')}
              className={`px-6 py-4 font-bold transition-all ${
                activeTab === 'users'
                  ? 'bg-purple-600 text-white border-b-4 border-purple-400'
                  : 'text-gray-400 hover:text-white hover:bg-gray-700/50'
              }`}
            >
              <Users className="h-5 w-5 inline mr-2" />
              Users
            </button>
            <button
              onClick={() => setActiveTab('trades')}
              className={`px-6 py-4 font-bold transition-all ${
                activeTab === 'trades'
                  ? 'bg-purple-600 text-white border-b-4 border-purple-400'
                  : 'text-gray-400 hover:text-white hover:bg-gray-700/50'
              }`}
            >
              <TrendingUp className="h-5 w-5 inline mr-2" />
              Trade Control
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Analytics Tab */}
        {activeTab === 'analytics' && analytics && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-white mb-4">Platform Analytics</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-gray-800 rounded-xl p-6 border border-purple-500/30">
                <div className="flex items-center justify-between mb-4">
                  <Users className="h-8 w-8 text-blue-400" />
                </div>
                <p className="text-gray-400 text-sm mb-1">Total Users</p>
                <h3 className="text-3xl font-bold text-white">{analytics.total_users}</h3>
              </div>

              <div className="bg-gray-800 rounded-xl p-6 border border-purple-500/30">
                <div className="flex items-center justify-between mb-4">
                  <DollarSign className="h-8 w-8 text-green-400" />
                </div>
                <p className="text-gray-400 text-sm mb-1">Platform Balance</p>
                <h3 className="text-3xl font-bold text-white">
                  {analytics.total_platform_balance.toLocaleString()} USDT
                </h3>
              </div>

              <div className="bg-gray-800 rounded-xl p-6 border border-purple-500/30">
                <div className="flex items-center justify-between mb-4">
                  <TrendingUp className="h-8 w-8 text-purple-400" />
                </div>
                <p className="text-gray-400 text-sm mb-1">Active Binary Trades</p>
                <h3 className="text-3xl font-bold text-white">
                  {analytics.binary_trades.active}
                </h3>
                <p className="text-xs text-gray-500 mt-1">
                  {analytics.binary_trades.total} total
                </p>
              </div>

              <div className="bg-gray-800 rounded-xl p-6 border border-purple-500/30">
                <div className="flex items-center justify-between mb-4">
                  <Crown className="h-8 w-8 text-yellow-400" />
                </div>
                <p className="text-gray-400 text-sm mb-1">Active Investments</p>
                <h3 className="text-3xl font-bold text-white">
                  {analytics.arbitrage_investments.active}
                </h3>
                <p className="text-xs text-gray-500 mt-1">
                  {analytics.arbitrage_investments.total} total
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-white">User Management</h2>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                />
              </div>
            </div>

            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin h-12 w-12 border-4 border-purple-500 border-t-transparent rounded-full mx-auto"></div>
              </div>
            ) : (
              <div className="bg-gray-800 rounded-xl overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-700">
                    <tr>
                      <th className="text-left py-4 px-6 text-gray-300 font-bold">User</th>
                      <th className="text-right py-4 px-6 text-gray-300 font-bold">Balance</th>
                      <th className="text-center py-4 px-6 text-gray-300 font-bold">Status</th>
                      <th className="text-center py-4 px-6 text-gray-300 font-bold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user.id} className="border-b border-gray-700 hover:bg-gray-700/50">
                        <td className="py-4 px-6">
                          <div>
                            <p className="font-bold text-white">{user.name || user.email}</p>
                            <p className="text-sm text-gray-400">{user.email}</p>
                          </div>
                        </td>
                        <td className="py-4 px-6 text-right">
                          {editingBalance === user.id ? (
                            <input
                              type="number"
                              defaultValue={user.balance}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  handleUpdateBalance(user.id, e.target.value, user.balance);
                                }
                              }}
                              className="w-32 px-2 py-1 bg-gray-900 border border-purple-500 rounded text-white text-right"
                              autoFocus
                            />
                          ) : (
                            <span className="font-bold text-green-400">
                              ${user.balance.toLocaleString()}
                            </span>
                          )}
                        </td>
                        <td className="py-4 px-6 text-center">
                          <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                            user.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                          }`}>
                            {user.status || 'active'}
                          </span>
                        </td>
                        <td className="py-4 px-6 text-center">
                          <button
                            onClick={() => setEditingBalance(editingBalance === user.id ? null : user.id)}
                            className="p-2 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors"
                          >
                            <Edit2 className="h-4 w-4 text-white" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Trade Control Tab */}
        {activeTab === 'trades' && (
          <div className="space-y-8">
            {/* Binary Trades Control */}
            <div>
              <h2 className="text-2xl font-bold text-white mb-4">Binary Trades Control</h2>
              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin h-12 w-12 border-4 border-purple-500 border-t-transparent rounded-full mx-auto"></div>
                </div>
              ) : binaryTrades.length === 0 ? (
                <div className="bg-gray-800 rounded-xl p-8 text-center">
                  <p className="text-gray-400">No active binary trades</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {binaryTrades.map((trade) => (
                    <div key={trade.id} className="bg-gray-800 rounded-xl p-6 border border-gray-700">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-4 mb-2">
                            <span className="text-lg font-bold text-white">
                              {formatPair(trade.crypto_symbol)}
                            </span>
                            <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                              trade.prediction === 'up' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                            }`}>
                              {trade.prediction.toUpperCase()}
                            </span>
                            <span className="text-sm text-gray-400">
                              Duration: {trade.duration_seconds}s
                            </span>
                          </div>
                          <div className="grid grid-cols-4 gap-4 text-sm">
                            <div>
                              <p className="text-gray-400">Amount</p>
                              <p className="font-bold text-white">{fmtFixed(trade.amount, 2)} USDT</p>
                            </div>
                            <div>
                              <p className="text-gray-400">Entry Price</p>
                              <p className="font-bold text-white">${fmtFixed(trade.entry_price, 2)}</p>
                            </div>
                            <div>
                              <p className="text-gray-400">Potential Profit</p>
                              <p className="font-bold text-yellow-400">+{fmtFixed(trade.potential_profit, 2)} USDT</p>
                            </div>
                            <div>
                              <p className="text-gray-400">User ID</p>
                              <p className="font-bold text-gray-300 text-xs">{trade.user_id.substring(0, 8)}...</p>
                            </div>
                          </div>
                        </div>
                        <div className="flex space-x-2 ml-6">
                          <button
                            onClick={() => handleControlTrade(trade.id, 'win')}
                            className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-bold rounded-lg transition-colors flex items-center space-x-2"
                          >
                            <Check className="h-5 w-5" />
                            <span>SET WIN</span>
                          </button>
                          <button
                            onClick={() => handleControlTrade(trade.id, 'lose')}
                            className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-bold rounded-lg transition-colors flex items-center space-x-2"
                          >
                            <X className="h-5 w-5" />
                            <span>SET LOSE</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* AI Arbitrage Investments Control */}
            <div>
              <h2 className="text-2xl font-bold text-white mb-4">AI Arbitrage Investments Control</h2>
              {investments.length === 0 ? (
                <div className="bg-gray-800 rounded-xl p-8 text-center">
                  <p className="text-gray-400">No active investments</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {investments.map((inv) => (
                    <div key={inv.id} className="bg-gray-800 rounded-xl p-6 border border-gray-700">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-4 mb-2">
                            <span className="text-lg font-bold text-yellow-400">
                              {inv.package_name}
                            </span>
                            <span className="text-sm text-gray-400">
                              {inv.period_days} days
                            </span>
                          </div>
                          <div className="grid grid-cols-4 gap-4 text-sm">
                            <div>
                              <p className="text-gray-400">Amount</p>
                              <p className="font-bold text-white">{inv.amount.toLocaleString()} USDT</p>
                            </div>
                            <div>
                              <p className="text-gray-400">Expected Return</p>
                              <p className="font-bold text-green-400">+{fmtFixed(inv.expected_return, 2)} USDT</p>
                            </div>
                            <div>
                              <p className="text-gray-400">Total Payout</p>
                              <p className="font-bold text-yellow-400">{fmtFixed(inv.total_payout, 2)} USDT</p>
                            </div>
                            <div>
                              <p className="text-gray-400">User ID</p>
                              <p className="font-bold text-gray-300 text-xs">{inv.user_id.substring(0, 8)}...</p>
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-col space-y-2 ml-6">
                          <button
                            onClick={() => handleControlInvestment(inv.id, 'win', 1.0)}
                            className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white font-bold rounded-lg transition-colors"
                          >
                            WIN (100%)
                          </button>
                          <button
                            onClick={() => {
                              const multiplier = prompt('Enter profit multiplier (0.0 to 2.0):', '0.5');
                              if (multiplier) handleControlInvestment(inv.id, 'win', multiplier);
                            }}
                            className="px-6 py-2 bg-yellow-600 hover:bg-yellow-700 text-white font-bold rounded-lg transition-colors"
                          >
                            WIN (Custom)
                          </button>
                          <button
                            onClick={() => handleControlInvestment(inv.id, 'lose', 1.0)}
                            className="px-6 py-2 bg-orange-600 hover:bg-orange-700 text-white font-bold rounded-lg transition-colors"
                          >
                            LOSE (Return 100%)
                          </button>
                          <button
                            onClick={() => {
                              const multiplier = prompt('Enter return multiplier (0.0 to 1.0):', '0.5');
                              if (multiplier) handleControlInvestment(inv.id, 'lose', multiplier);
                            }}
                            className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white font-bold rounded-lg transition-colors"
                          >
                            LOSE (Partial)
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
